import { useEffect, useState } from "react";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import {
  registerSingleAsset,
  fetchAssetFormOptions,
} from "../model/AssetUpload";

// Max length of each field (same as the database / backend)
export const FIELD_LIMITS = {
  category: 100,
  warranty_status: 20,
  is_wired: 20,
  product_id_model_no: 100,
  laptop_desktop_serial_no: 100,
  brand: 100,
  series: 100,
  sub_series: 100,
  type: 100,
  machine_type_model: 100,
  graphics: 200,
  processor: 200,
  ssd_hdd: 100,
  ram_gb: 100,
  operating_system: 100,
  charger_w: 100,
  product_id_charger: 100,
  vendor: 100,
};

// Fixed dropdown values (same as backend)
export const ASSET_STATUS_OPTIONS = ["Available", "Maintenance", "Sold", "Obsolete"];
export const WARRANTY_STATUS_OPTIONS = ["Active", "Expired"];
export const WIRED_OPTIONS = ["Wired", "Wireless"];

// All 20 fields of the bulk upload Excel. Status starts as "Available".
const EMPTY_FORM = {
  category: "",
  laptop_desktop_serial_no: "",
  date_purchase: "",
  brand: "",
  series: "",
  sub_series: "",
  type: "",
  product_id_model_no: "",
  machine_type_model: "",
  processor: "",
  ram_gb: "",
  ssd_hdd: "",
  graphics: "",
  operating_system: "",
  is_wired: "",
  charger_w: "",
  product_id_charger: "",
  vendor: "",
  warranty_date: "",
  warranty_status: "",
  asset_status: "Available",
};

// Today as YYYY-MM-DD (for the date picker limits)
export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

// "15/10/2026" -> "2026-10-15" (null if not a real date)
export const toIso = (text) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text || "");
  if (!m) return null;
  const [, d, mo, y] = m;
  const test = new Date(Number(y), Number(mo) - 1, Number(d));
  if (
    test.getFullYear() !== Number(y) ||
    test.getMonth() !== Number(mo) - 1 ||
    test.getDate() !== Number(d)
  ) {
    return null;
  }
  return `${y}-${mo}-${d}`;
};

// "2026-10-15" -> "15/10/2026"
export const isoToDisplay = (iso) => {
  if (!iso) return "";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
};

// Checks done in the browser before sending
const validate = (form, sizeId) => {
  const e = {};
  if (!sizeId) e.selectedSizeId = "Sticker size is required";
  if (!form.category.trim()) e.category = "Category is required";
  if (!form.laptop_desktop_serial_no.trim())
    e.laptop_desktop_serial_no = "Serial no is required";
  // Dates are typed as DD/MM/YYYY
  const purchaseIso = toIso(form.date_purchase);
  const warrantyIso = toIso(form.warranty_date);
  if (!form.date_purchase) {
    e.date_purchase = "Purchase date is required";
  } else if (!purchaseIso) {
    e.date_purchase = "Enter a valid date (DD/MM/YYYY)";
  } else if (purchaseIso > todayStr()) {
    e.date_purchase = "Purchase date cannot be in the future";
  }
  if (form.warranty_date && !warrantyIso) {
    e.warranty_date = "Enter a valid date (DD/MM/YYYY)";
  } else if (warrantyIso && purchaseIso && warrantyIso < purchaseIso) {
    e.warranty_date = "Warranty date cannot be before the purchase date";
  }
  return e;
};

const SingleAssetVM = ({ onRegistered }) => {
  const { handleToastOpen } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedSizeId, setSelectedSizeId] = useState("");
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({});

  // Values already used (category, brand, OS...) for the suggestions
  const loadOptions = async () => {
    try {
      const response = await fetchAssetFormOptions();
      setOptions(response.data?.data || {});
    } catch (error) {
      setOptions({}); // suggestions are optional, the form still works
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const clientErrors = validate(form, selectedSizeId);
  const isReady = Object.keys(clientErrors).length === 0;

  // Red message under a field: backend error first, otherwise browser check (after the user left the field)
  const getError = (key) =>
    serverErrors[key] || (touched[key] ? clientErrors[key] : "") || "";

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value ?? "" }));
    if (serverErrors[key]) {
      setServerErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const handleSizeChange = (value) => {
    setSelectedSizeId(value);
    setTouched((prev) => ({ ...prev, selectedSizeId: true }));
  };

  // Clear the form (sticker size stays selected)
  const handleClear = () => {
    setForm(EMPTY_FORM);
    setTouched({});
    setServerErrors({});
  };

  const handleRegister = async () => {
    const errors = validate(form, selectedSizeId);
    if (Object.keys(errors).length > 0) {
      const allTouched = {};
      Object.keys(errors).forEach((k) => (allTouched[k] = true));
      setTouched((prev) => ({ ...prev, ...allTouched }));
      handleToastOpen("error", Object.values(errors)[0]);
      return;
    }

    // Send only filled fields (trimmed)
    const payload = { selectedSizeId };
    Object.entries(form).forEach(([key, value]) => {
      const v = typeof value === "string" ? value.trim() : value;
      if (v) payload[key] = v;
    });
    // Send dates as YYYY-MM-DD (same format the Excel upload saves)
    if (payload.date_purchase) payload.date_purchase = toIso(payload.date_purchase);
    if (payload.warranty_date) payload.warranty_date = toIso(payload.warranty_date);

    try {
      setSaving(true);
      const response = await registerSingleAsset(payload);
      handleToastOpen(
        "success",
        response.data?.message || "Asset registered successfully"
      );
      handleClear();
      loadOptions(); // a new category / brand shows in suggestions next time
      if (onRegistered) await onRegistered();
    } catch (error) {
      const data = error.response?.data;
      if (data?.errors) {
        setServerErrors(data.errors); // e.g. "This serial no is already registered"
      }
      if (error.response) {
        handleToastOpen("error", data?.message || `Error ${error.response.status}`);
      } else if (error.request) {
        handleToastOpen("error", "No response from server. Please try again.");
      } else {
        handleToastOpen("error", error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    options,
    selectedSizeId,
    saving,
    isReady,
    getError,
    handleChange,
    handleBlur,
    handleSizeChange,
    handleClear,
    handleRegister,
  };
};

export default SingleAssetVM;